import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
// import Link from "next/link";
import styles from "./NewBlogPost.module.scss";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  TwitterShareButton,
  LinkedinShareButton,
  FacebookShareButton,
} from "react-share";
import { Field, Form, Formik, ErrorMessage } from "formik";
import FormInput from "@/Components/pages/VendorRegistration/form/FormInput";
import Button from "@/Components/Button";

import { parseHtml, toWebp, useScrollspy } from "@/utils/common";
const BlogFaq = dynamic(() => import("./faq"), { ssr: true });
const BlogCta = dynamic(() => import("../blog-cta"), { ssr: true });
const BlogAuthor = dynamic(() => import("../author"), { ssr: true });
const ConnectNowModal = dynamic(
  () => import("@/common/modals/ConnectNowModal"),
  { ssr: false },
);
const GetResourceRateModal = dynamic(
  () => import("@/common/modals/GetResourceRateModal"),
  { ssr: false },
);
const GetBusinessModal = dynamic(
  () => import("@/common/modals/GetBusinessModal"),
  { ssr: false },
);

interface Props {
  block: any;
  faq: any;
  authors: any;
}
// Reusable error display component (matches RFP pattern)
const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);

// Validation schema matching the RFP form pattern
const connectPopupValidationSchema = Yup.object({
  full_name: Yup.string().trim().required("Full name is required."),
  email: Yup.string()
    .trim()
    .email("Email is not valid.")
    .max(100, "Email is too long")
    .required("Email is required.")
    .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
  project_summary: Yup.string().trim().required("Message is required."),
});

const BlogDetailPost: React.FC<Props> = ({ block, faq, authors }) => {
  const router = useRouter();
  const keyPoints = block?.Keypoint || [];
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqObject = {
    id: "blog-faq-key-points",
    class: "blog-faq-key-points",
    display_title: faq?.short_title,
  };

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);

  // Let's Connect Popup logic
  const [showConnectPopup, setShowConnectPopup] = useState(false);
  const [hasPopupShown, setHasPopupShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (hasPopupShown) return;

      const scrollY = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      const scrollDepth = scrollY / (scrollHeight - clientHeight);

      if (scrollDepth >= 0.7) {
        setShowConnectPopup(true);
        setHasPopupShown(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasPopupShown]);

  useEffect(() => {
    if (
      faq?.faq?.length &&
      !keyPoints.find((ele: any) => ele.id === faqObject.id)
    ) {
      keyPoints.push(faqObject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build unified sections array so Introduction and FAQ are also tracked
  const allSections = useMemo(
    () => [
      { id: "introduction" },
      ...keyPoints
        .filter((_: any, i: number) => i !== 0)
        .filter((k: any) => k.id !== faqObject.id),
      ...(faq?.faq?.length ? [faqObject] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const activeIndex = useScrollspy(allSections, 150, "introduction", "id");
  const itemRefs: any = useRef({});
  const containerRef: any = useRef(null);

  const handleScrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    // On mobile/tablet screens, the TOC is layouted above/below the article content and is not sticky,
    // so we should not auto-scroll the TOC items as it would disrupt the user's reading/scrolling.
    if (typeof window !== "undefined" && window.innerWidth < 1024) return;

    const container = containerRef.current;
    const el = itemRefs.current[activeIndex];

    if (!container || !el) return;

    const parent = el.closest(".custom-scroll-blog") as HTMLElement | null;
    if (!parent) return;

    const containerRect = container.getBoundingClientRect();
    const threshold = 150;

    const inViewport =
      containerRect.top + threshold < window.innerHeight &&
      containerRect.bottom - threshold > 0;

    if (inViewport) {
      const timeoutId = setTimeout(() => {
        const parentRect = parent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        // Only scroll the TOC container if the item is not fully visible
        if (elRect.top < parentRect.top || elRect.bottom > parentRect.bottom) {
          const relativeTop = el.offsetTop - parent.offsetTop;
          const targetScroll =
            relativeTop - parent.clientHeight / 2 + el.clientHeight / 2;

          parent.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: "smooth",
          });
        }
      }, 120);

      return () => clearTimeout(timeoutId);
    }
  }, [activeIndex]);

  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";
  const siteUrl = process.env.NEXT_PUBLIC_REACT_APP_BASE_URL || "";
  const fullShareUrl = `${siteUrl}${router.asPath}`;

  return (
    <>
      <div className="w-full bg-white py-[25px] md:py-[48px] xl:py-[48px] relative z-0">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-[70%_30%] gap-6 md:gap-8 lg:gap-10">
            {/* LEFT CONTENT (70%) */}
            <article className="space-y-8 order-1 lg:order-0">
              <section>
                {keyPoints?.[0]?.text && (
                  <div
                    className={`my-[40px] space-y-6 new-blog-post ${styles.newBlogPost}`}
                    id="introduction"
                  >
                    {parseHtml(keyPoints?.[0]?.text)}
                  </div>
                )}

                <div ref={containerRef} className="blogcontent-detail">
                  {keyPoints
                    ?.filter((item: any) => item.id !== faqObject.id)
                    ?.map(
                      (item: any, index: any) =>
                        index !== 0 && (
                          <div
                            key={index}
                            className={`my-[40px] space-y-6 new-blog-post ${styles.newBlogPost}`}
                            id={item.id}
                          >
                            <a
                              id={`${item.id}-anchor`}
                              className="anchor-top"
                            />

                            <h2>{item.title}</h2>

                            {item?.Image?.data?.attributes?.url ? (
                              <Image
                                src={toWebp(
                                  `${baseUrl}${item?.Image?.data.attributes.url}`,
                                )}
                                alt={`${item?.Image?.data.attributes.alternativeText}`}
                                className="w-full object-cover"
                                width={800}
                                height={600}
                                quality={100}
                              />
                            ) : null}

                            {item?.text ? parseHtml(item?.text) : null}

                            {item?.cta ? (
                              <BlogCta
                                block={item?.cta}
                                handleShowConnect={() =>
                                  setShowConnectModal(true)
                                }
                                handleShowRate={() => setShowRateModal(true)}
                                handleShowBusiness={() =>
                                  setShowBusinessModal(true)
                                }
                              />
                            ) : null}
                          </div>
                        ),
                    )}

                  {faq?.faq?.length && (
                    <div
                      className={`my-[40px] space-y-6 new-blog-post ${styles.newBlogPost} ${faqObject.class}`}
                      id={faqObject.id}
                    >
                      <a id={`${faqObject.id}-anchor`} className="anchor-top" />
                      <BlogFaq items={faq} />
                    </div>
                  )}
                </div>
              </section>
            </article>

            {/* RIGHT SIDEBAR (30%) */}
            <aside className="relative order-0 lg:order-1">
              <div className="sticky top-[120px]">
                <div className="flex flex-col gap-4">
                  {/* TOC BOX */}
                  {keyPoints?.length > 1 && (
                    <div className="bg-blue-50 md:p-[32px_12px] p-[16px_12px] w-full">
                      <div className="flex flex-col">
                        <h4 className="text-secondary-text font-[700] xl:text-[20px] xl:leading-[26px] md:text-[18px] md:leading-[24px] text-[18px] leading-[20.8px] mb-3">
                          Table of Contents
                        </h4>

                        {/* Introduction */}
                        <a
                          href="#introduction"
                          ref={(el) => {
                            if (el) itemRefs.current["introduction"] = el;
                          }}
                          className={`cursor-pointer mb-[8px] text-[18px] leading-[27px] transition-colors duration-200 ${
                            activeIndex === "introduction"
                              ? "text-[#0086e6] font-semibold"
                              : "text-secondary-text"
                          }`}
                          onClick={(e) =>
                            handleScrollToSection(e, "introduction")
                          }
                        >
                          Introduction
                        </a>

                        <ul className="space-y-[16px] font-regular text-secondary-text text-subtitle-3 max-h-[70vh] overflow-y-auto custom-scroll-blog pr-2">
                          {keyPoints
                            ?.filter(
                              (item: any, index: number) =>
                                index !== 0 && item.id !== faqObject.id,
                            )
                            ?.map((item: any, index: any) => (
                              <li
                                key={index}
                                className="cursor-pointer hover:text-black"
                                ref={(el) => {
                                  if (el) itemRefs.current[item.id] = el;
                                }}
                              >
                                <a
                                  className={`transition-colors duration-200 ${
                                    activeIndex === item.id
                                      ? "text-[#0086e6] font-semibold"
                                      : ""
                                  }`}
                                  href={`#${item.id}`}
                                  onClick={(e) =>
                                    handleScrollToSection(e, item.id)
                                  }
                                >
                                  {item.display_title || item.title}
                                </a>
                              </li>
                            ))}
                        </ul>

                        {/* FAQ */}
                        {faq?.faq?.length > 0 && (
                          <a
                            href={`#${faqObject.id}`}
                            ref={(el) => {
                              if (el) itemRefs.current[faqObject.id] = el;
                            }}
                            className={`cursor-pointer mt-4 transition-colors duration-200 ${
                              activeIndex === faqObject.id
                                ? "text-[#0086e6] font-semibold"
                                : "text-secondary-text"
                            }`}
                            onClick={(e) =>
                              handleScrollToSection(e, faqObject.id)
                            }
                          >
                            FAQ
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SHARE BOX */}
                  <div className="bg-blue-50 flex items-center justify-between">
                    <span className="p-[12px_16px] w-[50%] font-regular text-secondary-text text-subtitle-3 border-r border-[#8AC7F4]">
                      Share On
                    </span>

                    <div className="flex items-center justify-end gap-5 p-[12px_16px] w-[50%]">
                      <LinkedinShareButton url={fullShareUrl}>
                        <Image
                          src="/images/blog/linkedin.svg"
                          alt="linkedin"
                          width={32}
                          height={32}
                          className="md:w-[32px] md:h-[32px] w-[24px] h-[24px]"
                        />
                      </LinkedinShareButton>

                      <TwitterShareButton url={fullShareUrl}>
                        <Image
                          src="/images/blog/x.svg"
                          alt="X"
                          width={32}
                          height={32}
                          className="md:w-[32px] md:h-[32px] w-[24px] h-[24px]"
                        />
                      </TwitterShareButton>

                      <FacebookShareButton url={fullShareUrl}>
                        <Image
                          src="/images/blog/facebook.svg"
                          alt="facebook"
                          width={32}
                          height={32}
                          className="md:w-[32px] md:h-[32px] w-[24px] h-[24px]"
                        />
                      </FacebookShareButton>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>

          {/* AUTHOR SECTION OUTSIDE GRID */}
          <div className="mt-10">
            <BlogAuthor data={authors} />
          </div>
        </div>
      </div>

      {/* Let's Connect Popup (Triggered by Scroll) */}
      {showConnectPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 transition-opacity duration-300">
          <div className="relative w-full max-w-[450px] bg-white overflow-hidden p-[32px_16px] md:p-[40px_32px]">
            {/* Background Image Layer */}
            <div className="absolute inset-0 z-0 bg-[#F0F8FF] bg-[url('/images/lets-connect.png')] bg-no-repeat bg-cover bg-[0%_0%]"></div>

            {/* Close Button */}
            <button
              onClick={() => setShowConnectPopup(false)}
              className="absolute top-4 right-4 z-50 text-gray-500 hover:text-gray-800"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="relative z-10">
              <Formik
                initialValues={{
                  full_name: "",
                  email: "",
                  project_summary: "",
                }}
                validationSchema={connectPopupValidationSchema}
                onSubmit={async (values, actions) => {
                  try {
                    setIsSubmitting(true);
                    // Mirror the same submission behaviour as RFP form
                    const form_data = new FormData();
                    form_data.append("full_name", values.full_name);
                    form_data.append("email", values.email);
                    form_data.append("project_summary", values.project_summary);
                    form_data.append("page_link", window.location.href);
                    form_data.append(
                      "page_title",
                      document.title ?? "Trootech",
                    );
                    actions.resetForm();
                    setShowConnectPopup(false);
                    toast.success("Thank you! We will get back to you soon.");
                  } catch (error) {
                    console.error("Connect Popup Submit error:", error);
                    toast.error("Something went wrong. Please try again.");
                  } finally {
                    setIsSubmitting(false);
                    actions.setSubmitting(false);
                  }
                }}
              >
                {({ errors, submitCount }) => (
                  <Form noValidate>
                    <h5 className="text-heading-6 font-extrabold text-primary-text text-left mb-[4px]">
                      Let&apos;s Connect
                    </h5>
                    <p className="text-left text-secondary-text font-regular text-subtitle-3 mb-[32px]">
                      Engage with ideas and stay updated on key trends.
                    </p>

                    <div className="flex flex-col gap-4">
                      <FormInput
                        name="full_name"
                        placeholder="Full name"
                        required
                      />
                      <FormInput
                        name="email"
                        type="email"
                        placeholder="Email"
                        required
                      />

                      <div className="textarea input-group mb-0">
                        <Field
                          as="textarea"
                          name="project_summary"
                          id="project_summary"
                          className={`input h-28 min-h-[140px]${
                            submitCount > 0 && errors.project_summary
                              ? " border-red-500"
                              : ""
                          }`}
                          placeholder=" "
                          aria-label="Message"
                          aria-describedby="popup-project_summary-error"
                          aria-required="true"
                        />
                        <label
                          htmlFor="project_summary"
                          className="text-subtitle-5 font-regular"
                        >
                          Message*
                        </label>
                        <ErrorMessage
                          name="project_summary"
                          component={ErrorMessageComponent}
                          id="popup-project_summary-error"
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <Button
                        type="submit"
                        variant="small"
                        loading={isSubmitting}
                        className="my-0 w-full text-subtitle-3"
                        label="Submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}

      {/* Legacy Modals */}
      <ConnectNowModal
        show={showConnectModal}
        handleClose={() => setShowConnectModal(false)}
      />
      <GetResourceRateModal
        show={showRateModal}
        handleClose={() => setShowRateModal(false)}
      />
      <GetBusinessModal
        show={showBusinessModal}
        handleClose={() => setShowBusinessModal(false)}
      />
    </>
  );
};

export default BlogDetailPost;
